import React, {useEffect, useState} from 'react'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import * as relativeTime from 'dayjs/plugin/relativeTime'
import {TagBox, TagLabel} from "./TagBox.js"
import getLogger from "./Logging.js"
import styles from "./AccountUtils.module.css"
import {useSolv} from "./SolvProvider";
import {useIntlEx} from "./IntlUtils";
import {shortIdEqual} from "./StringUtils";
import {useLocation} from "react-router-dom";
import {uuidRegex} from "./CommonUtils";
import {FormHeader} from "./FormComps";

const log = getLogger("AccountUtils")

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

export function AccountHeader(props) {
  const {intl} = useIntlEx()
  return (
    <div className="d-flex align-items-center gap-1">
      <div>
        {props.tenant.tenantName}
      </div>
      <div>
        <TagBox>
          {
            (props.tenant.accessingAs("*/DOWNSTREAM/*") || props.tenant.accessingAs("*/INSTREAM/RESELLER,SYSTEM")) &&
              <TagLabel label={intl.msg(`account_type_${props.tenant.tenantTypeId.toLowerCase()}`)} tooltip={""} size="lg"/>
          }
          {
            ["INVITING", "ONBOARDING"].includes(props.tenant.status) &&
              <AccountStatusLabel status={props.tenant.status} size="lg"/>
          }
        </TagBox>
      </div>
    </div>
  )
}

export function AccountStatusLabel(props) {
  let status

  if ("string" === typeof(props)) {
    status = props
  }
  else {
    status = props.status || props.tenantStatus
  }
  if (!status) {
    return (<></>)
  }
  let statusLabel = getStatusName(status)
  return (
    <TagLabel label={statusLabel} className={styles[`account-status-color-${statusLabel}`]} tooltip={statusLabel} size={props.size}/>
  )
}

export function AccountTabs(props) {

  const [tenant, setTenant] = useState(null)
  const {intl } = useIntlEx()

  const location = useLocation()

  const [tabs, setTabs] = useState(null)
  const [currentTab, setCurrentTab] = useState(null)

  const cmp = location.pathname.split("/").filter(i => i.trim().length > 0)
  const currentPage = cmp.findLast(i => !i.match(uuidRegex) && !i.startsWith("_"))

  // // log.debug(">>>Z: currentTab=", currentPage)

  useEffect(() => {
    // // log.debug(">>>Z: AccountTabs: useEffect: invoked: currentPage=", currentPage)
    if (props && props.tenant && !["INVITING", "ONBOARDING"].includes(props.tenant.status)) {

      const t = props.tenant
      // // log.debug(">>>Z: AccountTabs: tenant=", t)

      setTenant({...t})

      // // log.debug(">>>Z: AccountTabs: accessingAs=", t.accessingAs())

      const tbs = []

      if (!t.accessingAs("*/INSTREAM/*")) {

        const tenantId = t.tenantId?.replace(/\-/g, "")

        tbs.push({
          id: "account",
          title: t.tenantName,
          image: t.logoUrl,
          tag: t?.tenantTypeId?.charAt(0) || "",
          href: `/${tenantId}`
        })

        if (t.accessingAs("*/DOWNSTREAM/*")) {

          if (t.status !== "INVITING") {

            if (t.isReseller()) {
              tbs.push({
                id: "clients",
                href: `/${tenantId}/clients`
              })

              if (t.hasSubTenants()) {
                tbs.push({
                  id: "resellers",
                  href: `/${tenantId}/resellers`
                })
              }
            }

            if (t.isAffiliate() || t.isClient() || t.isReseller() || t.isSystem()) {
              tbs.push({
                id: "broadcasts",
                href: `/${tenantId}/broadcasts`
              })
            }
          }

          if (t.status !== "INVITING") {

            tbs.push({
              id: "members",
              href: `/${tenantId}/members`
            })

            if (t.isAffiliate() || t.isClient() || t.isReseller() || t.isSystem()) {
              tbs.push({
                id: "credits",
                href: `/${tenantId}/credits`
              })
            }

            // if (t.isReseller() || t.isSystem()) {
            //   tbs.push({
            //     id: "refcodes",
            //     href: `/${tenantId}/refcodes`
            //   })
            // }

            if (t.isAffiliate() || t.isReseller()) {
              tbs.push({
                id: "commissions",
                href: `/${tenantId}/commissions`
              })
            }
          }
        }

        setTabs(tbs)
        setCurrentTab(tbs.find(i => i.id === currentPage) || tbs[0])

      }

      // // log.debug(">>>Z: AccountTabs: tabs=", tbs)

    }
  }, [props])

  function Tab({tab}) {
    let title = tab.title || intl.msg(tab.id)
    return (
      <FormHeader.Tab key={tab.key} id={tab.id} href={tab.href} title={title} active={currentTab.id === tab.id}/>
    )
  }

  return (
    tabs?.length > 0 ? (
      <FormHeader.Tabs key={props.key || "account-tabs"}>
        <>
          {
            tabs.map((tab, i) => {
              return (
                <>
                  <Tab key={`form-tab-${i + 1}`} tab={tab}/>
                  {
                    i === 0 &&
                      <li><span style={{color: "var(--menu-border-color)"}}>|</span></li>
                  }
                </>
              )
            })
          }
        </>
      </FormHeader.Tabs>
    ) : (
      <></>
    )
  )
}


export function mkAccountDocUrl(user, tenant, docType, docId) {
  let suffix = `/${docType}${docId ? `/${docId}` : ""}`
  // log.debug("mkAccountDocUrl: user=, tenant=", user, tenant)
  if (user && tenant) {
    if (user.tenantId?.replace(/\-/g, "") === tenant.tenantId?.replace(/\-/g, "")) {
      return suffix
    }
    else {
      return `/${tenant.tenantId}${suffix}`
    }
  }
  else {
    return suffix
  }
}

function getStatusName(status) {
  switch (status) {
    case "NEW":
    case "ACTIVE":
    case "BANNED":
    case "BLOCKED":
    case "INACTIVE":
    case "INVITING":
    case "ONBOARDING":
      return status.toLowerCase()
    default:
      if (status?.startsWith("UNUSED")) {
        return "unused"
      }
      else if (status?.startsWith("DORMANT")) {
        return "dormant"
      }
      else {
        return "unknown"
      }
  }
}

export function useAccountStatusName() {

}

export function useAccountStatusOpts() {
  const {intl} = useIntlEx()
  const accountStatusOpts = [
    {
      value: "NEW",
      label: intl.msg("status_account_new"),
    },
    {
      value: "ACTIVE",
      label: intl.msg("status_account_active"),
    },
    {
      value: "INACTIVE",
      label: intl.msg("status_account_inactive"),
    },
    {
      value: "DORMANT",
      label: intl.msg("status_account_dormant"),
    },
    {
      value: "UNUSED",
      label: intl.msg("status_account_unused"),
    },
    {
      value: "BANNED",
      label: intl.msg("status_account_banned"),
    },
    {
      value: "ONBOARDING",
      label: intl.msg("status_account_onboarding"),
    },
    {
      value: "INVITING",
      label: intl.msg("status_account_inviting"),
    },
  ]
  function findAccountStatusOpt(status) {
    const st = status?.toUpperCase()
    if (!st) {
      return null
    }
    if (st.startsWith("DORMANT")) {
      return accountStatusOpts.find(o => o.value === "DORMANT")
    }
    else if (st.startsWith("UNUSED")) {
      return accountStatusOpts.find(o => o.value === "UNUSED")
    }
    else {
      return accountStatusOpts.find(o => o.value === st)
    }
  }
  return ({
    accountStatusOpts,
    findAccountStatusOpt,
  })
}

export function useAccountTypesOpts() {
  const {intl} = useIntlEx()
  const accountTypeOpts = [
    {
      value: "CLIENT",
      label: intl.msg("account_type_client"),
    },
    {
      value: "RESELLER",
      label: intl.msg("account_type_reseller"),
    },
    {
      value: "SYSTEM",
      label: intl.msg("account_type_system"),
    },
  ]
  function findAccountTypeOpt(status) {
    const st = status?.toUpperCase()
    if (!st) {
      return null
    }
    else {
      return accountTypeOpts.find(o => o.value === st)
    }
  }
  return ({
    accountTypeOpts: accountTypeOpts,
    findAccountTypeOpt: findAccountTypeOpt,
  })
}

function concatPaths(p1, p2) {
  let u = ""
  if (p1) {
    if (p1.endsWith("/")) {
      u = p1.substring(0, p1.length - 1)
    }
    else {
      u = p1
    }
  }
  if (p2) {
    if (p2.startsWith("/")) {
      u = u + p2
    }
    else {
      u = u + "/" + p2
    }
  }
  return u
}

export function mkHref({prefix, suffix, tenant, user}) {
  if (user) {
    if (shortIdEqual(user.tenantId, tenant.tenantId)) {
      return concatPaths(prefix, suffix)
    }
    else {
      return concatPaths(prefix, `/${tenant.tenantId.replaceAll("-", "")}${suffix}`)
    }
  }
  else {
    if (tenant && tenant.accessingAs("*/INSTREAM/*")) {
      return concatPaths(prefix, suffix)
    }
    else {
      return concatPaths(prefix, `/${tenant.tenantId.replaceAll("-", "")}${suffix}`)
    }
  }
}

export function useTenantPlan(type, filter) {
  const {intl} = useIntlEx()

  let tenantPlanOpts = []

  switch( type) {
    case "CLIENT":
      tenantPlanOpts = [
        {
          value: "DEMO",
          label: intl.msg("account_plan_demo")
        },
        {
          value: "BUSINESS_A",
          label: intl.msg("account_plan_business_a")
        },
        {
          value: "FUNDED",
          label: intl.msg("account_plan_funded")
        },
      ]
      break
    case "RESELLER":
      tenantPlanOpts = [
        {
          value: "HOUSE",
          label: intl.msg("account_plan_house")
        },
        {
          value: "COMMISSION",
          label: intl.msg("account_plan_commission")
        },
      ]
      break
    default:
      tenantPlanOpts = [
        {
          value: "BUSINESS_A",
          label: intl.msg("account_plan_business_a")
        },
        {
          value: "BUSINESS_B",
          label: intl.msg("account_plan_business_b")
        },
        {
          value: "FUNDED",
          label: intl.msg("account_plan_funded")
        },
        {
          value: "DEMO",
          label: intl.msg("account_plan_demo")
        },
        {
          value: "HOUSE",
          label: intl.msg("account_plan_house")
        },
        {
          value: "COMMISSION",
          label: intl.msg("account_plan_commission")
        },
        {
          value: "TRIAL",
          label: intl.msg("account_plan_trial")
        },
      ]
  }

  if (Array.isArray(filter)) {
    tenantPlanOpts = tenantPlanOpts.filter((c) => filter.includes(c.value))
  } else if ("function" === typeof (filter)) {
    tenantPlanOpts = tenantPlanOpts.map(filter).filter(Boolean)
  }

  return ({
    tenantPlanOpts: tenantPlanOpts,
    findTenantPlanOpt: (v, def) => {
      return tenantPlanOpts.find(e => e.value?.toUpperCase() === v)
        || tenantPlanOpts.find(e => e.value?.toUpperCase() === def)
    }
  })
}

export function useIndustry(filter) {
  const {intl} = useIntlEx()

  let industryOpts = [
    {
      value: "PUBLIC_SAFETY",
      label: intl.msg("industry_public_safety")
    },
    {
      value: "SME",
      label: intl.msg("industry_sme")
    },
    {
      value: "REAL_ESTATE",
      label: intl.msg("industry_real_estate")
    },
    {
      value: "OTHER",
      label: intl.msg("industry_other")
    },
  ]

  if (Array.isArray(filter)) {
    industryOpts = industryOpts.filter((c) => filter.includes(c.value))
  }
  else if ("function" === typeof(filter)) {
    industryOpts = industryOpts.map(filter).filter(Boolean)
  }

  return ({
    industryOpts: industryOpts,
    findIndustryOpt: (v, def) => {
      return industryOpts.find(e => e.value?.toUpperCase() === v)
             || industryOpts.find(e => e.value?.toUpperCase() === def)
    }
  })

}

export function useResellerType(filter) {

  const {session} = useSolv()
  const {intl} = useIntlEx()

  let resellerTypeOpts = [
    {
      value: "MASTER",
      label: intl.msg(`reseller_type_master`),
    },
    {
      value: "SUPER",
      label: intl.msg(`reseller_type_super`),
    },
    {
      value: "SUB",
      label: intl.msg(`reseller_type_sub`),
    },
    {
      value: "STANDALONE",
      label: intl.msg(`reseller_type_standalone`),
    },
  ]

  resellerTypeOpts.sort((a, b) => a.label.localeCompare(b.label))

  function getResellerType(tenant) {
    let n = null
    if (tenant && tenant.tenantTypeId === "RESELLER") {
      if (["DEMO", "HOUSE", "TEST"].includes(tenant.tenantPlanId)) {
        n = tenant.tenantPlanId
      }
      else {
        if (tenant.parentId) {
          if (tenant.hasSubTenants()) {
            n = "SUPER"
          }
          else {
            n = "SUB"
          }
        }
        else if (tenant.hasSubTenants()) {
          n = "MASTER"
        }
        else {
          n = "STANDALONE"
        }
      }
    }
    return n
  }

  function getChangeableResellerTypeOpts(tenant) {
    if (!tenant) {
      return []
    }
    switch (tenant.tenantPlanId) {
      case "BUSINESS_A":
      case "COMMISSION":
      case "RESELLER":
        switch (getResellerType(tenant)) {
          case "MASTER":
            return [
              findResellerTypeOpt("MASTER"),
              findResellerTypeOpt("STANDALONE")
            ].slice().sort((a, b) => a.label.localeCompare(b.label))
          case "SUPER":
            return [
              findResellerTypeOpt("MASTER"),
              findResellerTypeOpt("STANDALONE"),
              findResellerTypeOpt("SUPER"),
            ].slice().sort((a, b) => a.label.localeCompare(b.label))
          case "SUB":
            return [
              findResellerTypeOpt("STANDALONE"),
              findResellerTypeOpt("SUB"),
            ].slice().sort((a, b) => a.label.localeCompare(b.label))
          default:
            return [
              findResellerTypeOpt("MASTER"),
            ].slice().sort((a, b) => a.label.localeCompare(b.label))
        }
      case "HOUSE":
        return [
          findResellerTypeOpt("MASTER"),
          findResellerTypeOpt("STANDALONE")
        ].slice().sort((a, b) => a.label.localeCompare(b.label))
      default:
        return []
    }
  }

  function getInvitableResellerTypeOpts(tenant) {
    if ("SYSTEM" === tenant?.tenantTypeId) {
      return [
        findResellerTypeOpt("MASTER"),
        findResellerTypeOpt("STANDALONE"),
      ].slice().sort((a, b) => a.label.localeCompare(b.label))
    }
    else {
      switch (getResellerType(tenant)) {
        case "MASTER":
          return [
            findResellerTypeOpt("SUPER"),
          ].slice().sort((a, b) => a.label.localeCompare(b.label))
        case "SUPER":
          return [
            findResellerTypeOpt("SUB"),
          ].slice().sort((a, b) => a.label.localeCompare(b.label))
        default:
          return []
      }
    }
  }

  function getListableResellerTypeOpts(tenant) {
    if ("SYSTEM" === tenant?.tenantTypeId) {
      return [
        findResellerTypeOpt("MASTER"),
        findResellerTypeOpt("SUB"),
        findResellerTypeOpt("SUPER"),
        findResellerTypeOpt("STANDALONE"),
      ].slice().sort((a, b) => a.label.localeCompare(b.label))
    }
    else {
      switch (getResellerType(tenant)) {
        case "MASTER":
          return [
            findResellerTypeOpt("SUB"),
            findResellerTypeOpt("SUPER"),
          ].slice().sort((a, b) => a.label.localeCompare(b.label))
        case "SUPER":
          return [
            findResellerTypeOpt("SUB"),
          ].slice().sort((a, b) => a.label.localeCompare(b.label))
        default:
          return []
      }
    }
  }
  
  if (Array.isArray(filter)) {
    resellerTypeOpts = resellerTypeOpts.filter((c) => filter.includes(c.value))
  }
  else if ("function" === typeof(filter)) {
    resellerTypeOpts = resellerTypeOpts.map(filter).filter(Boolean)
  }

  function findResellerTypeOpt(v, def) {
    return resellerTypeOpts.find(e => e.value?.toUpperCase() === v)
           || resellerTypeOpts.find(e => e.value?.toUpperCase() === def)
  }

  return {
    getResellerType,
    resellerTypeOpts,
    getChangeableResellerTypeOpts,
    getInvitableResellerTypeOpts,
    getListableResellerTypeOpts,
    findResellerTypeOpt
  }

}

export function useTaxType(filter) {
  const {intl} = useIntlEx()

  let taxTypeOpts = [
    {
      value: "GST",
      label: "GST"
    },
    {
      value: "SALES TAX",
      label: "SALES TAX"
    },
    {
      value: "VAT",
      label: "VAT"
    },
  ]

  if (Array.isArray(filter)) {
    taxTypeOpts = taxTypeOpts.filter((c) => filter.includes(c.value))
  }
  else if ("function" === typeof(filter)) {
    taxTypeOpts = taxTypeOpts.map(filter).filter(Boolean)
  }

  return ({
    taxTypeOpts: taxTypeOpts,
    findTaxTypeOpt: (v, def) => {
      return taxTypeOpts.find(e => e.value?.toUpperCase() === v)
        || taxTypeOpts.find(e => e.value?.toUpperCase() === def)
    }
  })
}

export function useClientSupportLevelOpts(filter) {
  const {intl} = useIntlEx()

  let clientSupportLevelOpts = [
    {
      value: "MAXIMUM",
      text: intl.msg("client_support_level_opt_maximum"),
      label: <>
        <div>{intl.msg("client_support_level_opt_maximum")}</div>
        <small>{intl.msg("client_support_level_opt_maximum_description")}</small>
      </>,
      level: 2,
    },
    {
      value: "ADVANCED",
      text: intl.msg("client_support_level_opt_advanced"),
      label: <>
        <div>{intl.msg("client_support_level_opt_advanced")}</div>
        <small>{intl.msg("client_support_level_opt_advanced_description")}</small>
      </>,
      level: 1,
    },
    {
      value: "STANDARD",
      text: intl.msg("client_support_level_opt_standard"),
      label: <>
        <div>{intl.msg("client_support_level_opt_standard")}</div>
        <small>{intl.msg("client_support_level_opt_standard_description")}</small>
      </>,
      level: 0,
    },
  ]

  if (Array.isArray(filter)) {
    clientSupportLevelOpts = clientSupportLevelOpts.filter((c) => filter.includes(c.value))
  }
  else if ("function" === typeof(filter)) {
    clientSupportLevelOpts = clientSupportLevelOpts.map(filter).filter(Boolean)
  }

  return ({
    clientSupportLevelOpts,
    findClientSupportLevelOpt: (v, def) => {
      return clientSupportLevelOpts.find(e => e.value?.toUpperCase() === v)
        || clientSupportLevelOpts.find(e => e.value?.toUpperCase() === def)
    },
    findClientSupportLevelOptByLevel: (v, def) => {
      return clientSupportLevelOpts.find(e => e.level === v)
        || clientSupportLevelOpts.find(e => e.level === def)
    }
  })
}

export function hasChildTenants(tenant) {
  return (tenant?.maxDepth ? tenant?.maxDepth > (tenant?.parentPath ? (tenant?.parentPath.split(".").length - 1) : 0) : false)
}

export const PATH_PAT = /^\/?(?:([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|[a-f0-9]{32}))\/([^_]+)(?:\/([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|[a-f0-9]{32}|_[a-zA-Z0-9_]+))?$/;

export const PATH_START_PAT = /^\/([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|[a-f0-9]{32})/;

export const PATH_END_PAT = /\/([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|[a-f0-9]{32}|_[a-zA-Z0-9_]+)$/;

export const TENANT_PATH_PAT = /^\/(?:(?:[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})|[0-9a-fA-F]{32})/;

export function parsePath(path) {
  const match = path.match(PATH_PAT)
  // log.debug(`parsePath: path=${path}, match=${match}`)
  return ({
    tenantId: match[1] ? match[1].replace(/-/g, '') : null,
    documentPath: match[2] ? match[2].replace(/\/$/, '') : null,
    documentId: match[3] ? match[3].replace(/_/g, '') : null
  })
}

export function EnsureAccessible(props) {

  const {setFatal} = useSolv()
  const [tenant, setTenant] = useState(null)

  useEffect(() => {
    if (props.tenant) {
      setTenant(props.tenant)
    }
  }, [props.tenant])

  useEffect(() => {
    if (tenant) {
      if (props.accessingAs) {
        if (!tenant.accessingAs(props.accessingAs)) {
          setFatal("DENIED_ACCESS")
        }
      }
    }
  }, [tenant])

}

export function TenantLink(props) {
  const {intl} = useIntlEx()

  log.debug(">>>Z: TenantLink: props=", props)

  const tenantId = props?.tenant && props.tenant.tenantId ? props.tenant.tenantId : props?.tenantId ? props.tenantId : null
  const tenantName = props?.tenant && props.tenant.tenantName ? props.tenant.tenantName : props?.tenantName ? props.tenantName : null
  const href = `/${tenantId.replace(/\-/g, "")}${props.suffix ? props.suffix : ""}`

  if (tenantId && tenantName) {
    return (
      props.withTags ? (
        <div className="d-flex flex-row flex-wrap gap-1">
          <a className="text-link" href={href}>
            {tenantName}
          </a>
          <TagBox>
            {
              props?.tenant.tenantType &&
                <TagLabel label={props?.tenant?.tenantType} size="md"/>
            }
            {
              shortIdEqual(props.user?.tenantId, tenantId) &&
                <TagLabel label={intl.msg("you")} size="md"/>
            }
          </TagBox>
        </div>
      ) : (
        <a className="text-link" href={href}>
          {tenantName}
        </a>
      )
    )
  }
  else {
    return (<></>)
  }
}

export function navigateTo(props) {
  window.location = mkHref(props)
}